import React from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha, keyframes } from "@mui/material/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AuthContext from "../../../contexts/AuthContext";
import { login } from "../../../utilities/authFlow";

const popIn = keyframes`
  0% { transform: scale(.6); opacity: 0; }
  60% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); }
`;

// Shown after a successful email confirmation. If the password is available in
// this session (they signed up / entered the code in the same tab) we log them
// straight in. If not (magic link opened in a fresh browser), we show a single
// prefilled password field so signing in is one step, not a dead-end.
const Confirmed = ({ setPage, pw }) => {
  const theme = useTheme();
  const { email, appStyle, setSignedIn } = React.useContext(AuthContext);
  const [password, setPassword] = React.useState("");
  const [passwordVisibility, setPasswordVisibility] = React.useState(false);
  const [error, setError] = React.useState("");
  const [loggingIn, setLoggingIn] = React.useState(false);
  const [autoFailed, setAutoFailed] = React.useState(false);
  const attempted = React.useRef(false);

  const doLogin = async (pwToUse) => {
    setLoggingIn(true);
    setError("");
    const result = await login(email, pwToUse);
    if (result.success) {
      setSignedIn(true);
    } else {
      setLoggingIn(false);
      // Drop out of the auto-login spinner so the manual password field +
      // error are shown (otherwise a failed auto-login spins forever).
      setAutoFailed(true);
      if (pwToUse) setPassword(pwToUse);
      setError(result.error || "That password didn't match. Please try again.");
    }
  };

  // Direct login when we already have the password in this session.
  React.useEffect(() => {
    if (pw && !attempted.current) {
      attempted.current = true;
      const timer = setTimeout(() => doLogin(pw), 1200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pw]);

  const autoMode = Boolean(pw) && !autoFailed;
  const primary = theme.palette.primary.main;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 5,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: "22px",
          p: { xs: 3, sm: 4 },
          textAlign: "center",
        }}
      >
        {appStyle?.logo && (
          <Box component="img" src={appStyle.logo} alt="" sx={{ height: 84, mb: 1 }} />
        )}

        <Box
          sx={{
            width: 84,
            height: 84,
            borderRadius: "50%",
            background: alpha(primary, 0.12),
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mt: 1,
            animation: `${popIn} .5s cubic-bezier(.2,.8,.2,1)`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: "50%",
              background: primary,
              display: "grid",
              placeItems: "center",
            }}
          >
            <CheckRoundedIcon sx={{ color: "#fff", fontSize: 34 }} />
          </Box>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, mt: 2.5 }}>
          You're confirmed!
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
          Your email has been verified.
        </Typography>

        {autoMode ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mt: 4, mb: 1 }}>
            <CircularProgress size={26} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Signing you in...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 3.5, textAlign: "left" }}>
            <Typography variant="body2" align="center" sx={{ color: "text.secondary", mb: 2 }}>
              {email ? (
                <>
                  Enter your password to sign in as{" "}
                  <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                    {email}
                  </Box>
                </>
              ) : (
                "Enter your password to sign in."
              )}
            </Typography>

            {error && (
              <Typography variant="subtitle2" align="center" sx={{ color: primary, mb: 1.5 }}>
                {error}
              </Typography>
            )}

            <TextField
              autoFocus
              fullWidth
              label="Password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && password) doLogin(password);
              }}
              type={passwordVisibility ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={() => setPasswordVisibility(!passwordVisibility)}
                  >
                    {passwordVisibility ? (
                      <VisibilityOff style={{ cursor: "pointer" }} />
                    ) : (
                      <Visibility style={{ cursor: "pointer" }} />
                    )}
                  </InputAdornment>
                ),
              }}
              autoComplete="current-password"
            />

            <Button
              fullWidth
              variant="contained"
              disabled={!password || loggingIn}
              onClick={() => doLogin(password)}
              sx={{ mt: 2, height: 44, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
            >
              {loggingIn ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Sign in"}
            </Button>

            <Button
              fullWidth
              onClick={() => setPage("login")}
              sx={{ mt: 1, height: 40, color: primary, textTransform: "none" }}
            >
              Use a different account
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Confirmed;
